import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePaging } from 'hooks/usePaging';
import { SIZE_VALIDATION_ASSET_MODEL } from 'const/paging';
import { RoomLevelIssues } from 'types/room';
import { SearchType } from 'types/loctions';
import { fromPairs } from 'lodash';
import { getListRoomLevelIssues, ParamsGetListRoomLevelIssues } from 'services/report';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { actionExportRoomIssues } from 'services/locations';
import { useFlash } from 'contexts/flash';
import { useRoomContext } from 'contexts/RoomContext';
import { ListOptions } from 'app/pages/RoomReportViewPhotoPage/hook';

const EXPORT_QUEUED_MESSAGE =
  'Report generation has been queued. Download the file from the Background Jobs tab when it is ready.';

type IssuesRoomView = 'active' | 'archived';

export const useIssuesRoomTab = (view: IssuesRoomView = 'active') => {
  const isArchivedView = view === 'archived';
  const { setFlash } = useFlash();
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState<string>('');
  const [order, setOrder] = useState<'desc' | 'asc' | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');
  const [searchType, setSearchType] = useState<SearchType>('partial-text');
  const [filterRoomIssues, setFilterRoomIssues] = useState<
    {
      key: string;
      value: string;
      label: string;
      level?: number;
    }[]
  >();
  const [dataRoomLevelIssues, setDataRoomLevelIssues] = useState<RoomLevelIssues[]>([]);
  const {
    pagingInfo: pagingInfoRoomLevelIssues,
    setCurrentPage: setCurrentPageInfoRoomLevelIssues,
    setTotal: setTotalInfoRoomLevelIssues
  } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });
  const { systemDropdownValues } = useRoomContext();
  const options: ListOptions = (systemDropdownValues || []) as unknown as ListOptions;

  const getInitRoomLevelIssues = useCallback(
    async (paramsProps: Partial<ParamsGetListRoomLevelIssues>) => {
      const data = await getListRoomLevelIssues({ params: paramsProps });
      setDataRoomLevelIssues(data.roomIssue);
      setTotalInfoRoomLevelIssues(data.total);
    },
    [setTotalInfoRoomLevelIssues]
  );

  const { asyncCallback: asyncGetDataRoomLevelIssues } = useAsyncCallback(
    getInitRoomLevelIssues,
    []
  );

  useEffect(() => {
    const filterParams = fromPairs(
      (filterRoomIssues ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page,
      keyword: searchText || undefined,
      sortBy: sortBy || undefined,
      order,
      searchType,
      archived: isArchivedView,
      ...filterParams
    };
    asyncGetDataRoomLevelIssues(params);
  }, [
    asyncGetDataRoomLevelIssues,
    filterRoomIssues,
    isArchivedView,
    order,
    page,
    searchText,
    sortBy,
    searchType
  ]);

  const onSearch = useCallback(
    (value: string) => {
      setPage(1);
      setCurrentPageInfoRoomLevelIssues(1);
      setSearchText(value);
    },
    [setCurrentPageInfoRoomLevelIssues]
  );

  const sortOption = useMemo(
    () => ({
      field: sortBy,
      order
    }),
    [order, sortBy]
  );

  const handleSort = useCallback(
    (orderBy: string) => {
      const sortByNow = sortBy;
      const orderNow = order;
      if (orderBy === sortByNow) {
        if (orderNow === 'asc') {
          setOrder('desc');
        } else {
          setOrder('asc');
        }
      } else {
        setSortBy(orderBy);
        setOrder('asc');
      }
    },
    [order, sortBy]
  );

  const handleChangePage = useCallback(
    (value: number) => {
      setCurrentPageInfoRoomLevelIssues(value);
      setPage(value);
    },
    [setCurrentPageInfoRoomLevelIssues]
  );

  const handleFilterRoomIssues = useCallback(
    async (value?: { key: string; value: string; label: string; level?: number }) => {
      if (!value) return;
      setPage(1);
      setCurrentPageInfoRoomLevelIssues(1);
      setFilterRoomIssues((prevState) => {
        let nextState: typeof prevState;

        if (!prevState) {
          nextState = [value];
        } else {
          const index = prevState.findIndex((item) => item.key === value.key);
          if (index !== -1) {
            const existingItem = prevState[index];
            if (
              (!value?.level && existingItem.value === value.value) ||
              Number(value?.level) === Number(existingItem?.level)
            ) {
              nextState = [...prevState.slice(0, index), ...prevState.slice(index + 1)];
            } else {
              const updated = [...prevState];
              updated[index] = value;
              nextState = updated;
            }
          } else {
            nextState = [...prevState, value];
          }
        }
        return nextState;
      });
    },
    [setCurrentPageInfoRoomLevelIssues]
  );

  const exportRoomIssues = async (
    type: string,
    imageOption?: string,
    columns?: string[],
    dropdownOptionColumns?: number[],
    singleFieldColumns?: number[]
  ) => {
    const filterParams = fromPairs(
      (filterRoomIssues ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page,
      keyword: searchText || undefined,
      sortBy: sortBy || undefined,
      order,
      exportType: type,
      imageOption: imageOption || '1st',
      columns,
      dropdownOptionColumns,
      singleFieldColumns,
      searchType,
      archived: isArchivedView,
      ...filterParams
    };
    const data = await actionExportRoomIssues(params);
    setFlash({
      type: 'success',
      message:
        data?.success && data?.jobId != null
          ? EXPORT_QUEUED_MESSAGE
          : data?.message || EXPORT_QUEUED_MESSAGE
    });
  };

  const { asyncCallback: asyncExportRoomIssues } = useAsyncCallback(exportRoomIssues, [
    filterRoomIssues,
    isArchivedView,
    order,
    page,
    searchText,
    sortBy,
    searchType,
    setFlash
  ]);

  const refreshDataRoomLevelIssues = useCallback(() => {
    const filterParams = fromPairs(
      (filterRoomIssues ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    asyncGetDataRoomLevelIssues({
      size: SIZE_VALIDATION_ASSET_MODEL,
      page,
      keyword: searchText || undefined,
      sortBy: sortBy || undefined,
      order,
      searchType,
      archived: isArchivedView,
      ...filterParams
    });
  }, [
    asyncGetDataRoomLevelIssues,
    filterRoomIssues,
    isArchivedView,
    order,
    page,
    searchText,
    sortBy,
    searchType
  ]);

  return {
    options,
    dataRoomLevelIssues,
    pagingInfoRoomLevelIssues,
    handleChangePage,
    handleSort,
    sortOption,
    onSearch,
    searchText,
    filterRoomIssues,
    setFilterRoomIssues: handleFilterRoomIssues,
    searchType,
    setSearchType,
    asyncExportRoomIssues,
    refreshDataRoomLevelIssues
  };
};
